<template>
    <div ref="scrollList" class="sc-message-list">
        <div v-if="showLoadMessageHistory" v-show="!messagesLoading" class="sc-load-more has-text-centered">
            <e-icon icon="sync" custom-class="is-clickable" @click="$emit('load-message-history')" />
        </div>
        <template v-for="(entry, idx) in datedMessages" :key="entry.message.id ?? idx">
            <div
                v-if="entry.dayLabel"
                data-test="chat-day-divider"
                class="tw:my-4 tw:flex tw:items-center tw:gap-3 tw:text-xs tw:text-slate-600"
            >
                <span aria-hidden="true" class="tw:h-px tw:flex-1 tw:bg-slate-300" />
                <time :datetime="entry.day" class="tw:shrink-0">{{ entry.dayLabel }}</time>
                <span aria-hidden="true" class="tw:h-px tw:flex-1 tw:bg-slate-300" />
            </div>
            <ChatMessage
                :message="entry.message"
                :chat-image-url="chatImageUrl(entry.message.author)"
                :author-name="authorName(entry.message.author)"
                :colors="colors"
                :message-styling="messageStyling"
            />
        </template>
        <ChatMessage
            v-show="showTypingIndicator !== null"
            :message="{ author: showTypingIndicator, type: 'typing' }"
            :chat-image-url="chatImageUrl(showTypingIndicator)"
            :colors="colors"
            :message-styling="messageStyling"
        />
    </div>
</template>
<script>
import ChatMessage from '@/components/chat/system/Message.vue';
import { chatDayLabel, chatTimestamp, nextChatDayDelay } from '@/utilities/chatTimestamps.js';

export default {
    components: { ChatMessage },

    props: {
        participants: {
            type: Array,
            required: true,
        },

        messages: {
            type: Array,
            required: true,
        },

        showTypingIndicator: {
            type: Number,
            default: null,
        },

        colors: {
            type: Object,
            required: true,
        },

        alwaysScrollToBottom: {
            type: Boolean,
            required: true,
        },

        messageStyling: {
            type: Boolean,
            required: true,
        },

        messagesLoading: {
            type: Boolean,
            default: false,
        },

        messageHistoryCount: {
            type: Number,
            default: 0,
        },
    },

    emits: ['load-message-history'],

    data() {
        return { calendarNow: Date.now(), dayTimer: null };
    },

    computed: {
        datedMessages() {
            let previousDay = null;
            return this.messages.map(message => {
                const timestamp = chatTimestamp(message.created_at, this.$timezone.name);
                const day = timestamp?.day;
                const dayLabel =
                    day && day !== previousDay ? chatDayLabel(day, this.$timezone.name, this.calendarNow) : '';
                if (day) previousDay = day;
                return { message, day, dayLabel };
            });
        },

        showLoadMessageHistory() {
            return this.messages.length < this.messageHistoryCount;
        },
    },

    mounted() {
        this.refreshCalendarDay();
        this._scrollDown();
    },

    beforeUnmount() {
        clearTimeout(this.dayTimer);
    },

    updated() {
        if (this.shouldScrollToBottom()) this.$nextTick(this._scrollDown());
    },

    methods: {
        refreshCalendarDay() {
            const scrollTop = this.dayTimer === null ? null : this.$refs.scrollList.scrollTop;
            this.calendarNow = Date.now();
            // A label-only refresh must preserve the reader's position in history.
            if (scrollTop !== null) {
                this.$nextTick(() => {
                    if (this.$refs.scrollList) this.$refs.scrollList.scrollTop = scrollTop;
                });
            }
            this.dayTimer = setTimeout(
                this.refreshCalendarDay,
                nextChatDayDelay(this.$timezone.name, this.calendarNow)
            );
        },

        _scrollDown() {
            this.$refs.scrollList.scrollTop = this.$refs.scrollList.scrollHeight;
        },

        shouldScrollToBottom() {
            return (
                this.alwaysScrollToBottom || this.$refs.scrollList.scrollTop > this.$refs.scrollList.scrollHeight - 600
            );
        },

        profile(author) {
            const profile = this.participants.find(participant => participant.id === author);

            // A profile may not be found for system messages or messages by authUser.id
            return profile || { photo_url: '', name: '' };
        },

        chatImageUrl(author) {
            return this.profile(author).photo_url;
        },

        authorName(author) {
            return this.profile(author).name;
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-message-list {
    height: 80%;
    overflow-y: auto;
    background-size: 100%;
    padding: $column-gap;
}

.sc-load-more {
    margin: $column-gap 0;
}

.sc-load-more i {
    cursor: pointer;
}

@media (max-width: 450px) {
    .sc-message-list {
        height: auto;
    }
}
</style>
